import React, { useState, useEffect } from 'react';
import { Package, Shield, TrendingDown, Users, Award, Heart, Truck, ThumbsUp, Star, Droplet, Sparkles, Droplets, Wind, Baby, Home, Gift, Shirt, UtensilsCrossed, WashingMachine, Bath, Pipette } from 'lucide-react';

const About = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [counters, setCounters] = useState({
    clients: 0,
    produits: 0,
    experience: 0,
    satisfaction: 0
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const animateCounter = (key, target, duration) => {
      const increment = target / (duration / 16);
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounters(prev => ({ ...prev, [key]: Math.floor(current) }));
      }, 16);

      return timer;
    };

    const timer1 = animateCounter('clients', 500, 2000);
    const timer2 = animateCounter('produits', 50, 2000);
    const timer3 = animateCounter('experience', 5, 2000);
    const timer4 = animateCounter('satisfaction', 98, 2000);

    return () => {
      clearInterval(timer1);
      clearInterval(timer2);
      clearInterval(timer3);
      clearInterval(timer4);
    };
  }, []);

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #000000, #0a0a0a)',
      color: '#ffffff',
      paddingTop: isMobile ? '2rem' : '3rem',
      paddingBottom: isMobile ? '3rem' : '5rem',
    },
    content: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '0 1.5rem' : '0 2rem',
    },
    hero: {
      textAlign: 'center',
      marginBottom: isMobile ? '3rem' : '5rem',
      animation: 'fadeInDown 0.8s ease-out',
    },
    title: {
      fontSize: isMobile ? '2.5rem' : '4rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '1rem',
      fontFamily: "'Montserrat', sans-serif",
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      color: '#cccccc',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: '1.8',
    },
    section: {
      marginBottom: isMobile ? '4rem' : '6rem',
    },
    sectionTitle: {
      fontSize: isMobile ? '2rem' : '2.5rem',
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: isMobile ? '1.5rem' : '2rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      justifyContent: isMobile ? 'center' : 'flex-start',
      textAlign: isMobile ? 'center' : 'left',
      flexWrap: 'wrap',
    },
    storySection: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(197, 155, 0, 0.15)',
      borderRadius: '25px',
      padding: isMobile ? '2rem' : '3rem',
      animation: 'fadeInUp 0.8s ease-out',
    },
    storyText: {
      fontSize: isMobile ? '1.05rem' : '1.15rem',
      lineHeight: '2',
      color: '#cccccc',
      marginBottom: '1.5rem',
    },
    highlightText: {
      color: '#c59b00',
      fontWeight: '600',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? '1.5rem' : '2rem',
      marginTop: isMobile ? '2rem' : '3rem',
      animation: 'fadeInUp 0.8s ease-out 0.2s backwards',
    },
    statCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(197, 155, 0, 0.15)',
      borderRadius: '20px',
      padding: isMobile ? '1.5rem' : '2rem',
      textAlign: 'center',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
    },
    statNumber: {
      fontSize: isMobile ? '2.5rem' : '3rem',
      fontWeight: '800',
      color: '#c59b00',
      marginBottom: '0.5rem',
      fontFamily: "'Montserrat', sans-serif",
    },
    statLabel: {
      fontSize: isMobile ? '0.95rem' : '1.1rem',
      color: '#cccccc',
      fontWeight: '500',
    },
    valuesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '1.5rem' : '2rem',
      animation: 'fadeInUp 0.8s ease-out 0.3s backwards',
    },
    valueCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(197, 155, 0, 0.15)',
      borderRadius: '20px',
      padding: isMobile ? '2rem' : '2.5rem',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
    },
    valueIconWrapper: {
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      width: isMobile ? '60px' : '70px',
      height: isMobile ? '60px' : '70px',
      borderRadius: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1.5rem',
      transition: 'transform 0.4s ease',
      boxShadow: '0 4px 15px rgba(197, 155, 0, 0.25)',
    },
    valueTitle: {
      fontSize: isMobile ? '1.4rem' : '1.6rem',
      fontWeight: '700',
      color: '#c59b00',
      marginBottom: '1rem',
    },
    valueText: {
      fontSize: isMobile ? '1rem' : '1.05rem',
      color: '#cccccc',
      lineHeight: '1.8',
    },
    productsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: isMobile ? '1rem' : '1.5rem',
      animation: 'fadeInUp 0.8s ease-out 0.4s backwards',
    },
    productCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(197, 155, 0, 0.15)',
      borderRadius: '15px',
      padding: isMobile ? '1.2rem' : '1.5rem',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    productIcon: {
      marginBottom: '0.8rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    productName: {
      fontSize: isMobile ? '0.9rem' : '1rem',
      fontWeight: '600',
      color: '#ffffff',
    },
    ctaSection: {
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      borderRadius: '25px',
      padding: isMobile ? '2.5rem 2rem' : '4rem 3rem',
      textAlign: 'center',
      animation: 'fadeInUp 0.8s ease-out 0.5s backwards',
      boxShadow: '0 8px 30px rgba(197, 155, 0, 0.35)',
    },
    ctaTitle: {
      fontSize: isMobile ? '1.8rem' : '2.5rem',
      fontWeight: '800',
      color: '#000000',
      marginBottom: '1rem',
      fontFamily: "'Montserrat', sans-serif",
    },
    ctaText: {
      fontSize: isMobile ? '1.05rem' : '1.2rem',
      color: '#000000',
      marginBottom: '2rem',
      opacity: 0.9,
    },
    ctaButton: {
      background: '#000000',
      color: '#c59b00',
      border: 'none',
      borderRadius: '12px',
      padding: isMobile ? '1rem 2rem' : '1.2rem 3rem',
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'inline-block',
      textDecoration: 'none',
    },
  };

  const StatCard = ({ number, label, suffix = '' }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        style={{
          ...styles.statCard,
          transform: isHovered ? 'translateY(-10px) scale(1.05)' : 'translateY(0) scale(1)',
          borderColor: isHovered ? '#c59b00' : 'rgba(197, 155, 0, 0.15)',
          background: isHovered ? 'rgba(197, 155, 0, 0.08)' : 'rgba(255, 255, 255, 0.03)',
          boxShadow: isHovered ? '0 12px 30px rgba(197, 155, 0, 0.25)' : 'none',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.statNumber}>{number}{suffix}</div>
        <div style={styles.statLabel}>{label}</div>
      </div>
    );
  };

  const ValueCard = ({ icon: Icon, title, description }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        style={{
          ...styles.valueCard,
          transform: isHovered ? 'translateY(-10px)' : 'translateY(0)',
          borderColor: isHovered ? '#c59b00' : 'rgba(197, 155, 0, 0.15)',
          background: isHovered ? 'rgba(197, 155, 0, 0.08)' : 'rgba(255, 255, 255, 0.03)',
          boxShadow: isHovered ? '0 12px 30px rgba(197, 155, 0, 0.25)' : 'none',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          style={{
            ...styles.valueIconWrapper,
            transform: isHovered ? 'rotate(360deg) scale(1.1)' : 'rotate(0deg) scale(1)',
          }}
        >
          <Icon size={isMobile ? 28 : 32} color="#000000" />
        </div>
        <h3 style={styles.valueTitle}>{title}</h3>
        <p style={styles.valueText}>{description}</p>
      </div>
    );
  };

  const ProductCard = ({ icon: Icon, name }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        style={{
          ...styles.productCard,
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          borderColor: isHovered ? '#c59b00' : 'rgba(197, 155, 0, 0.15)',
          background: isHovered ? 'rgba(197, 155, 0, 0.08)' : 'rgba(255, 255, 255, 0.03)',
          boxShadow: isHovered ? '0 8px 20px rgba(197, 155, 0, 0.25)' : 'none',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.productIcon}>
          <Icon size={isMobile ? 32 : 40} color="#c59b00" />
        </div>
        <div style={styles.productName}>{name}</div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.hero}>
            <h1 style={styles.title}>À PROPOS DE DESTOCKMAX</h1>
            <p style={styles.subtitle}>
              Votre partenaire de confiance pour des produits d'hygiène de qualité à prix imbattables. 
              Découvrez notre histoire, nos valeurs et notre engagement envers vous.
            </p>
          </div>

          <div style={styles.section}>
            <div style={styles.storySection}>
              <h2 style={styles.sectionTitle}>
                <Award size={isMobile ? 28 : 32} color="#c59b00" />
                Notre Histoire
              </h2>
              <p style={styles.storyText}>
                Fondée avec la <span style={styles.highlightText}>passion du commerce</span> et l'ambition de rendre les produits d'hygiène 
                de qualité accessibles à tous, <span style={styles.highlightText}>DESTOCKMAX</span> est née d'une vision simple : 
                offrir des solutions économiques sans compromis sur la qualité.
              </p>
              <p style={styles.storyText}>
                Spécialisés dans la <span style={styles.highlightText}>vente de palettes de déstockage</span>, nous proposons une large gamme 
                de produits d'hygiène essentiels - lessives, produits ménagers, liquides vaisselle, déodorants, couches Pampers, cosmétiques, 
                et bien plus encore. Notre objectif ? Vous permettre de faire des <span style={styles.highlightText}>économies substantielles</span> tout 
                en bénéficiant de marques reconnues et de produits de première qualité.
              </p>
              <p style={styles.storyText}>
                Que vous soyez <span style={styles.highlightText}>professionnel</span> (hôtels, restaurants, collectivités, associations) 
                ou <span style={styles.highlightText}>particulier</span> souhaitant optimiser votre budget familial, DESTOCKMAX est votre 
                allié pour des achats intelligents et rentables.
              </p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <TrendingDown size={isMobile ? 28 : 32} color="#c59b00" />
              Quelques Chiffres
            </h2>
            <div style={styles.statsGrid}>
              <StatCard number={counters.clients} label="Clients Satisfaits" suffix="+" />
              <StatCard number={counters.produits} label="Références Produits" suffix="+" />
              <StatCard number={counters.experience} label="Ans d'Expérience" />
              <StatCard number={counters.satisfaction} label="Taux de Satisfaction" suffix="%" />
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <Heart size={isMobile ? 28 : 32} color="#c59b00" />
              Nos Valeurs
            </h2>
            <div style={styles.valuesGrid}>
              <ValueCard
                icon={Shield}
                title="Qualité Garantie"
                description="Tous nos produits sont rigoureusement sélectionnés et vérifiés. Nous ne proposons que des articles de marques reconnues en parfait état."
              />
              <ValueCard
                icon={TrendingDown}
                title="Prix Imbattables"
                description="Grâce au déstockage, nous vous offrons des réductions exceptionnelles pouvant aller jusqu'à 70% par rapport aux prix du marché."
              />
              <ValueCard
                icon={Users}
                title="Service Client"
                description="Notre équipe dévouée est à votre écoute pour vous conseiller et vous accompagner dans vos choix. Votre satisfaction est notre priorité."
              />
              <ValueCard
                icon={Truck}
                title="Livraison Rapide"
                description="Nous assurons une logistique efficace pour une livraison rapide de vos palettes partout en France, dans les meilleurs délais."
              />
              <ValueCard
                icon={ThumbsUp}
                title="Transparence"
                description="Pas de frais cachés, pas de surprise. Nous croyons en une relation commerciale honnête et transparente avec nos clients."
              />
              <ValueCard
                icon={Star}
                title="Excellence"
                description="Nous nous efforçons constamment d'améliorer notre service et notre offre pour dépasser vos attentes à chaque commande."
              />
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <Package size={isMobile ? 28 : 32} color="#c59b00" />
              Nos Catégories de Produits
            </h2>
            <div style={styles.productsGrid}>
              <ProductCard icon={WashingMachine} name="Lessive" />
              <ProductCard icon={Sparkles} name="Produit de ménage" />
              <ProductCard icon={Droplets} name="Liquide vaisselle" />
              <ProductCard icon={Wind} name="Déodorant" />
              <ProductCard icon={Pipette} name="Hygiène corporelle" />
              <ProductCard icon={Baby} name="Couches Pampers" />
              <ProductCard icon={Star} name="Cosmétique" />
              <ProductCard icon={Droplet} name="Serviettes hygiéniques" />
              <ProductCard icon={Shirt} name="Entretien du linge" />
              <ProductCard icon={UtensilsCrossed} name="Entretien vaisselle" />
              <ProductCard icon={Bath} name="Produit WC" />
              <ProductCard icon={Sparkles} name="Entretien courant" />
              <ProductCard icon={Home} name="Entretien maison" />
              <ProductCard icon={Gift} name="Divers bazar" />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.ctaSection}>
              <h2 style={styles.ctaTitle}>
                Prêt à Économiser sur vos Achats ?
              </h2>
              <p style={styles.ctaText}>
                Découvrez notre catalogue complet et profitez de nos offres exceptionnelles dès maintenant !
              </p>
              <a
                href="/catalogue"
                style={styles.ctaButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Voir le Catalogue
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;